import React from 'react';
import Toggle from './Toggle';
import UploadIcon from './icons/UploadIcon';
import SpinnerIcon from './icons/SpinnerIcon';
import Tooltip from './Tooltip';
import InfoIcon from './icons/InfoIcon';
import type { FileInfo } from '../types';

interface FileControlsProps {
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInfo: FileInfo | null;
  onToggleUrlSafe: () => void;
  isUrlSafe: boolean;
  onTogglePadding: () => void;
  isPadded: boolean;
  isUploading: boolean;
}

const FileControls: React.FC<FileControlsProps> = ({
  onFileSelect,
  fileInfo,
  onToggleUrlSafe,
  isUrlSafe,
  onTogglePadding,
  isPadded,
  isUploading,
}) => {
  const buttonBaseClasses = "text-white font-bold py-2 px-4 rounded-md cursor-pointer transition-colors flex items-center";
  const buttonEnabledClasses = "bg-blue-600 hover:bg-blue-700";
  const buttonDisabledClasses = "bg-blue-400 cursor-not-allowed";

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-white rounded-lg border border-gray-200">
      <div className="flex items-center gap-4">
        <label className={`${buttonBaseClasses} ${isUploading ? buttonDisabledClasses : buttonEnabledClasses}`}>
          {isUploading ? (
            <>
              <SpinnerIcon className="mr-2 animate-spin h-4 w-4" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              <UploadIcon className="mr-2" />
              <span>Upload File</span>
            </>
          )}
          <input type="file" className="hidden" onChange={onFileSelect} disabled={isUploading} />
        </label>
        {fileInfo && (
          <div className="text-sm text-gray-500 font-mono">
            <span className="font-semibold text-gray-700">{fileInfo.name}</span>
            <span className="mx-2">|</span>
            <span>{(fileInfo.size / 1024).toFixed(2)} KB</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-x-6 gap-y-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Toggle label="URL-safe" enabled={isUrlSafe} onChange={onToggleUrlSafe} />
          <Tooltip text="Replaces '+' with '-' and '/' with '_' to make the string safe for URLs and filenames." className="w-[150px]">
            <span className="text-gray-400 hover:text-gray-600 cursor-help">
              <InfoIcon />
            </span>
          </Tooltip>
        </div>
        <div className="flex items-center gap-2">
          <Toggle label="Padding" enabled={isPadded} onChange={onTogglePadding} />
          <Tooltip text="Adds '=' characters at the end to make the string length a multiple of 4. Required by some decoders.">
            <span className="text-gray-400 hover:text-gray-600 cursor-help">
              <InfoIcon />
            </span>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default FileControls;
